import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import AdditionalSchemas, {
  normalizeAdditionalSchemas,
} from "../Seo/AdditionalSchemas";

const emptyGlobalSeo = {
  review_schema: "",
  organisation_schema: "",
  twitter_site: "",
  twitter_card: "",
  twitter_creator: "",
  og_site_name: "",
  additional_schemas: [],
};

const validateSchemas = (globalSeo) => {
  const schemas = [
    { label: "Review Schema", schema: globalSeo.review_schema },
    { label: "Organization Schema", schema: globalSeo.organisation_schema },
    ...normalizeAdditionalSchemas(globalSeo.additional_schemas).map((item) => ({
      label: item.label || "Additional Schema",
      schema: item.schema,
    })),
  ];

  return schemas.reduce((errors, item) => {
    if (/<\/?\s*script\b/i.test(item.schema)) {
      errors.push(`${item.label} should contain only JSON-LD content. Please remove the <script> tag.`);
    } else if (item.schema?.trim()) {
      try {
        JSON.parse(item.schema);
      } catch (error) {
        errors.push(`${item.label} must contain valid JSON-LD.`);
      }
    }
    return errors;
  }, []);
};

export default function SchemaManagement() {
  const [globalSeo, setGlobalSeo] = useState(emptyGlobalSeo);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchGlobalSeo = async () => {
      try {
        const response = await ApiRequest("GET", ApiEndpoints.globalSeo.view);
        setGlobalSeo({
          ...emptyGlobalSeo,
          ...(response?.schema || {}),
          additional_schemas: normalizeAdditionalSchemas(response?.schema?.additional_schemas),
        });
      } catch (error) {
        toast.error(error.response?.data?.message || "Unable to load Global SEO.");
      } finally {
        setLoading(false);
      }
    };

    fetchGlobalSeo();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setGlobalSeo((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateSchemas(globalSeo);
    setErrors(validationErrors);

    if (validationErrors.length) {
      toast.error("Please correct the Global SEO schema fields.");
      return;
    }

    setSaving(true);
    try {
      const response = await ApiRequest("POST", ApiEndpoints.globalSeo.update, globalSeo);
      toast.success(response.message);
    } catch (error) {
      const apiErrors = error.response?.data?.errors || [];
      setErrors(apiErrors.length ? apiErrors : ["Unable to update Global SEO."]);
      toast.error(error.response?.data?.message || "Unable to update Global SEO.");
    } finally {
      setSaving(false);
    }
  };

  const pagename = {
    name: "Global SEO",
    user: "Admin",
    innerpage: "Global SEO",
  };

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Twitter Site</label></div>
                  <input
                    type="text"
                    name="twitter_site"
                    value={globalSeo.twitter_site}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Twitter Card</label></div>
                  <input
                    type="text"
                    name="twitter_card"
                    value={globalSeo.twitter_card}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Twitter Creator (Twitter Account)</label></div>
                  <input
                    type="text"
                    name="twitter_creator"
                    value={globalSeo.twitter_creator}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>OG Site Name</label></div>
                  <input
                    type="text"
                    name="og_site_name"
                    value={globalSeo.og_site_name}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Review Schema</label></div>
                  <textarea
                    name="review_schema"
                    value={globalSeo.review_schema}
                    onChange={handleChange}
                    className="form-control"
                    rows="14"
                    disabled={loading || saving}
                    style={{ height: "auto", fontFamily: "monospace", whiteSpace: "pre", overflow: "auto" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add"><label>Organization Schema</label></div>
                  <textarea
                    name="organisation_schema"
                    value={globalSeo.organisation_schema}
                    onChange={handleChange}
                    className="form-control"
                    rows="14"
                    disabled={loading || saving}
                    style={{ height: "auto", fontFamily: "monospace", whiteSpace: "pre", overflow: "auto" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <AdditionalSchemas
            value={globalSeo.additional_schemas}
            onChange={(additional_schemas) =>
              setGlobalSeo((current) => ({ ...current, additional_schemas }))
            }
          />

          {errors.length > 0 && (
            <div className="alert alert-danger">
              {errors.map((error) => <div key={error}>{error}</div>)}
            </div>
          )}

          <button className="default-btn" type="submit" disabled={loading || saving}>
            {saving ? "Saving..." : "Save Global SEO"}<span></span>
          </button>
        </form>
      </div>
    </>
  );
}
